import { ChangeEvent } from 'react';

import { Field, FieldProps } from 'formik';

import { Label, LABEL_TYPE, PARAGRAPH2_NORMAL_CLASSES } from '@/components/generic';

type InputProps = {
  id: string;
  label: string;
  type: string;
  errorMessage?: string;
  isInvalid?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  width?: string;
  className?: string;
  hasIcon?: boolean;
  handleIconClick?: (value: string) => void;
  isTextEditorOpened?: boolean;
  handleFocus?: () => void;
  readOnly?: boolean;
  rightSideIcon?: JSX.Element;
  handleChange?: (value: string) => void; // Mandatory field for non-Formik usage
  value?: string; // Value for non-Formik usage
  maxLength?: number;
  min?: number;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  dataTestId?: string;
  autoComplete?: string;
};

export const Input = ({
  id,
  label,
  type,
  errorMessage,
  isInvalid,
  disabled,
  isRequired,
  placeholder,
  width,
  className,
  hasIcon,
  handleIconClick,
  isTextEditorOpened,
  handleFocus,
  readOnly = false,
  rightSideIcon,
  handleChange, // Used for non-Formik scenarios
  value, // Used for non-Formik scenarios
  maxLength,
  min,
  onBlur,
  onKeyDown,
  dataTestId,
  autoComplete
}: InputProps) => {
  // Function to handle changes in non-Formik scenarios
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (handleChange) {
      handleChange(e.target.value); // Call the custom handleChange if passed
    }
  };

  // TODO: Use KEYBOARD_KEYS constants
  const preventNonNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      type === 'number' &&
      !/^\d$/.test(e.key) &&
      e.key !== 'Backspace' &&
      e.key !== 'ArrowLeft' &&
      e.key !== 'ArrowRight'
    ) {
      e.preventDefault();
    }
  };

  return (
    <div className={`space-y-1.5 ${width ?? ''}`}>
      <div className='space-y-2.5'>
        {label && <Label text={`${label}${isRequired ? ' *' : ''}`} type={LABEL_TYPE.PARAGRAPH2} />}
        <div className='relative flex items-center'>
          {/* If handleChange is provided, assume we are not using Formik and manage value manually */}
          {handleChange ? (
            <input
              data-testid={dataTestId}
              id={id}
              type={type}
              value={value} // Controlled input for non-Formik usage
              maxLength={maxLength}
              min={min}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={onBlur}
              readOnly={readOnly}
              disabled={disabled}
              placeholder={placeholder ?? label}
              autoComplete={autoComplete}
              className={`box-border border border-solid bg-primary-white focus:outline-primary-blue ${PARAGRAPH2_NORMAL_CLASSES} w-full rounded-[10px] px-3.5 ${
                isInvalid
                  ? 'border-accent-red focus:outline-accent-red'
                  : 'border-secondary-blue-gray'
              } h-10 ${className}`}
              onKeyDown={(e) => {
                preventNonNumericInput(e);
                onKeyDown?.(e);
              }}
            />
          ) : (
            <Field name={id} resize={disabled}>
              {({ field }: FieldProps) => (
                <input
                  {...field}
                  data-testid={dataTestId}
                  id={id}
                  type={type}
                  min={min}
                  maxLength={maxLength}
                  onFocus={handleFocus}
                  readOnly={readOnly}
                  placeholder={placeholder ?? label}
                  autoComplete={autoComplete}
                  className={`box-border border border-solid bg-primary-white focus:outline-primary-blue ${PARAGRAPH2_NORMAL_CLASSES} w-full rounded-[10px] px-3.5 ${
                    isInvalid
                      ? 'border-accent-red focus:outline-accent-red'
                      : 'border-secondary-blue-gray'
                  } h-10 ${className}`}
                  onKeyDown={(e) => {
                    preventNonNumericInput(e);
                    onKeyDown?.(e);
                  }}
                />
              )}
            </Field>
          )}
          {rightSideIcon && (
            <div className='absolute inset-y-0 right-3 flex items-center'>{rightSideIcon}</div>
          )}
          {hasIcon && handleIconClick && (
            <span
              className='absolute right-3 cursor-pointer text-xl'
              onClick={() => handleIconClick(id)}>
              {isTextEditorOpened ? '-' : '+'}
            </span>
          )}
        </div>
      </div>
      {(isInvalid || errorMessage) && (
        <div className='flex h-4 justify-end'>
          <Label text={errorMessage} type={LABEL_TYPE.PARAGRAPH_ERROR} />
        </div>
      )}
    </div>
  );
};
